import { useLingui } from '@lingui/react/macro'
import { Input } from 'antd'
import { TextAreaRef } from 'antd/es/input/TextArea'
import { CantileverButton } from 'components/CantileverButton'
import { AttachmentPills } from 'features/chatbot/components/AttachmentPills'
import { DocumentAttachmentPopover } from 'features/chatbot/components/DocumentAttachmentPopover'
import { useChatbotAttachments } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { ChatbotDataContext } from 'features/chatbot/contexts/ChatbotDataLayer'
import { ChatbotUXContext } from 'features/chatbot/contexts/ChatbotUXLayer'
import { ArrowUp, Square } from 'lucide-react'
import React, { useContext, useRef, useState } from 'react'
import styled from 'styled-components'

const SUBMIT_BG = 'rgba(255, 255, 255, 0.15)'
const SUBMIT_HOVER_BG = 'rgba(255, 255, 255, 0.25)'
const SUBMIT_DISABLED_BG = 'rgba(255, 255, 255, 0.08)'

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  width: 100%;
  gap: 0.5rem;
`

const ContextSection = styled.div`
  padding: 0 0.25rem;
`

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledTextArea = styled(Input.TextArea)`
  width: 100%;
  border-radius: 0.5rem;
  padding: 0.75rem 2.5rem 0.75rem 2.5rem;
  background-color: ${({ theme }) => theme.forecast.background1} !important;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  &::placeholder {
    color: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:focus,
  &:focus-within {
    border-color: ${({ theme }) => theme.forecast.border2} !important;
    box-shadow: none !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.forecast.border2};
  }
`

const SubmitButton = styled(CantileverButton)`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  min-height: 1.5rem;
  width: 1.5rem;
  z-index: 1;
  background: ${SUBMIT_BG} !important;
  border: none !important;
  color: ${({ theme }) => theme.forecast.secondaryText} !important;

  &:hover:not(:disabled) {
    background: ${SUBMIT_HOVER_BG} !important;
    color: ${({ theme }) => theme.forecast.defaultText} !important;
  }

  &:disabled {
    background: ${SUBMIT_DISABLED_BG} !important;
    color: ${({ theme }) => theme.forecast.tertiaryText} !important;
  }
`

interface ChatbotInputCardProps {
  helpModalOpen: boolean
  setHelpModalOpen: (open: boolean) => void
}

export const ChatbotInputCard: React.FC<ChatbotInputCardProps> = () => {
  const { t } = useLingui()
  const { formRef, handleEnterKeyDown, handleSendMessage } =
    useContext(ChatbotUXContext)
  const { isThinking, cancelGeneration } = useContext(ChatbotDataContext)
  const [inputValue, setInputValue] = useState('')
  const attachmentContext = useChatbotAttachments()
  const textAreaRef = useRef<TextAreaRef>(null)

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isThinking) {
      cancelGeneration()
      return
    }
    if (inputValue.trim()) {
      handleSendMessage(inputValue)
      setInputValue('')
    }
  }

  const hasAttachments = attachmentContext?.attachments.length > 0

  return (
    <FormContainer
      ref={formRef}
      onSubmit={handleSubmit}
      onKeyDown={handleEnterKeyDown}>
      {hasAttachments && (
        <ContextSection>
          <AttachmentPills />
        </ContextSection>
      )}
      <InputWrapper>
        <DocumentAttachmentPopover
          onDocumentSelected={() => textAreaRef.current?.focus()}
        />
        <StyledTextArea
          ref={textAreaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t`Ask a question...`}
          autoSize={{ minRows: 1, maxRows: 6 }}
        />
        <SubmitButton
          iconOnly
          aria-label={isThinking ? t`Stop` : t`Submit`}
          onClick={handleSubmit}
          disabled={!isThinking && !inputValue.trim()}>
          {isThinking ? <Square size={14} /> : <ArrowUp size={16} />}
        </SubmitButton>
      </InputWrapper>
    </FormContainer>
  )
}
